import { addDays, endOfDay, isBefore, startOfDay } from 'date-fns';
import { RRule } from 'shared/lib/rrule';
import { HabitSchedule } from 'shared/types/habit-schedule';
import { Timestamp } from 'shared/types/timestamp';

import { getScheduleEndDate } from './get-schedule-end-date';
import { getTimeframeSchedules } from './get-timeframe-schedules';

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
};

export const getOccurrences = (
  schedules: HabitSchedule[],
  { startDate, endDate }: Options,
) => {
  const timeframeSchedules = getTimeframeSchedules(schedules, {
    startDate,
    endDate,
  });

  const occurrences = timeframeSchedules.reduce<Timestamp[]>(
    (acc, schedule) => {
      if (schedule.frequency?.unit) {
        return acc;
      }

      // if there are completions before the startDate, we take the first completion and start from there to calculate the occurrences
      const completionsBeforeStartDate = isBefore(
        startDate,
        startOfDay(schedule.startDate),
      )
        ? schedule.entries
            .filter((entry) =>
              isBefore(entry.date, startOfDay(schedule.startDate)),
            )
            .sort(
              (entryA, entryB) => entryA.date.getTime() - entryB.date.getTime(),
            )
        : [];
      if (completionsBeforeStartDate.length) {
        const earliestCompletion = completionsBeforeStartDate[0];

        const originalRule = RRule.fromString(schedule.rrule.format);

        const rrule = new RRule({
          ...originalRule.origOptions,
          dtstart: startOfDay(earliestCompletion.date),
          until: endOfDay(addDays(schedule.startDate, -1)),
        });

        acc.push(...rrule.between(startDate, endDate, true));
      }

      const scheduleEnd = getScheduleEndDate(schedule);
      const originalRule = RRule.fromString(schedule.rrule.format);

      const rrule = new RRule({
        ...originalRule.origOptions,
        dtstart: startOfDay(schedule.startDate),
        until: endOfDay(
          scheduleEnd && isBefore(scheduleEnd, endDate) ? scheduleEnd : endDate,
        ),
      });

      acc.push(...rrule.between(startDate, endDate, true));

      return acc;
    },
    [],
  );

  const uniqueOccurrences = new Set(
    occurrences.map((occurrence) => startOfDay(occurrence).getTime()),
  );

  return Array.from(uniqueOccurrences).map((occurrence) =>
    endOfDay(new Date(occurrence)),
  );
};
